import { type ElementRef, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { BotMessageSquare, User } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

export enum ChatUser {
  USER = 'user',
  BOT = 'bot',
}

const chatAvatarVariants = cva('bg-primary text-primary-foreground', {
  variants: {
    size: {
      sm: 'h-5 w-5 [&>span>svg]:h-3.5 [&>span>svg]:w-3.5',
      md: 'h-8 w-8',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export interface ChatAvatarProps
  extends VariantProps<typeof chatAvatarVariants> {
  user: ChatUser;
  src?: string;
  alt?: string;
}

const ChatAvatar = forwardRef<ElementRef<typeof Avatar>, ChatAvatarProps>(
  ({ user, src, alt, size }, ref) => (
    <Avatar
      ref={ref}
      className={cn(chatAvatarVariants({ size }))}
      data-user={user}>
      <AvatarImage
        className='bg-transparent text-transparent'
        src={src}
        alt={alt}
      />

      <AvatarFallback className='bg-primary text-primary-foreground'>
        {user === ChatUser.USER ? <User /> : <BotMessageSquare />}
      </AvatarFallback>
    </Avatar>
  )
);
ChatAvatar.displayName = 'ChatAvatar';

export default ChatAvatar;
